import { ValueFormatterParams } from "ag-grid-community"
import {
  ControlReport,
  ControlThresholds,
  CorrectionEntry,
  MatchStatus,
  NavControlResult,
  NavReference,
  PositionDiff,
  ReconciliationResult,
  ShareClassNavResult,
  ShareClassOverride,
  ShareCountAcceptance,
  ShareCountControlResult,
} from "../../../../api/reconciliation"

export type RowOverride = {
  override_price?: number
  override_quantity?: number
  // Fee rows are corrected by amount — direct market value (issue #1630).
  override_market_value?: number
  comment?: string
}

// One edited shares outstanding (nombre de parts) for a share class (#1631).
export type ShareClassEdit = {
  nombre_parts?: number
  comment?: string
}

// One share-count acceptance pending submit (CONTEXT.md, "Share-count
// acceptance"): the operator took the file's count for the class, with an
// optional comment. Keyed by class code in the page's edit state.
export type ShareCountAcceptanceEdit = {
  comment: string
}

export const getStatusLabel = (
  status: MatchStatus,
  sourceA: string,
  sourceB: string,
): string => {
  switch (status) {
    case "paired":
      return "Paired"
    case "source_a_only":
      return `${sourceA} Only`
    case "source_b_only":
      return `${sourceB} Only`
  }
}

// Directional break colors: red = Scient holds something CACEIS doesn't,
// orange = CACEIS holds something Scient doesn't. The color names the book
// to investigate.
export const STATUS_COLORS: Record<MatchStatus, string> = {
  paired: "#2D72D2",
  source_a_only: "#CD4246",
  source_b_only: "#EC9A3C",
}

const MATCH_COLOR = "#238551"
const MISMATCH_COLOR = "#C87619"

/**
 * AG Grid cellStyle for value cells on paired rows.
 * Green text if the associated diff is 0, amber if non-zero.
 * Unmatched rows get no special styling.
 */
export const matchCellStyle = (diffField: keyof PositionDiff) => {
  return (params: any): Record<string, string> | undefined => {
    const data = params.data as PositionDiff | undefined
    if (!data || data.match_status !== "paired") return undefined
    const diff = data[diffField] as number | null
    const color = diff === 0 ? MATCH_COLOR : MISMATCH_COLOR
    return { color }
  }
}

/**
 * Format a number with thousands separators.
 * Shows decimals only when the value has a fractional part.
 */
export const formatNumber = (value: number | string | null | undefined): string => {
  if (value == null) return "-"
  const num = Number(value)
  const hasDecimals = num % 1 !== 0
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  })
}

/**
 * AG Grid value formatter: integers with thousands separators.
 */
export const numberFormatter = (params: ValueFormatterParams): string => {
  return formatNumber(params.value)
}

/**
 * AG Grid value formatter: trade-ccy MV with the row's ISO currency suffix
 * (currencies vary per row, so the unit must travel with the value).
 */
export const localMvFormatter = (params: ValueFormatterParams): string => {
  if (params.value == null) return "-"
  const currency = (params.data as PositionDiff | undefined)?.currency
  const formatted = formatNumber(params.value)
  return currency ? `${formatted} ${currency}` : formatted
}

/**
 * AG Grid value formatter: FX rate, 4 decimal places, no thousands noise.
 */
export const fxFormatter = (params: ValueFormatterParams): string => {
  if (params.value == null) return "-"
  return Number(params.value).toFixed(4)
}

/**
 * AG Grid value formatter: 2-4 decimal places.
 */
export const decimalFormatter = (params: ValueFormatterParams): string => {
  if (params.value == null) return "-"
  return Number(params.value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })
}

// Groups that read better pluralized: "fee" holds the merged fee+accrual
// lines, "option" the equity options split out of the equity group.
const ASSET_TYPE_GROUP_LABELS: Record<string, string> = {
  fee: "Fees",
  option: "Options",
}

/**
 * Render an asset_type code (e.g. "structured_product") as a human-friendly
 * group label by replacing underscores with spaces and capitalising each word.
 */
export const formatAssetType = (value: string | null): string => {
  if (!value) return "Other"
  const groupLabel = ASSET_TYPE_GROUP_LABELS[value]
  if (groupLabel) return groupLabel
  return value
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

/**
 * Price / quantity edits are allowed on Scient-side rows, except fee rows:
 * fee lines are merged per label (quantity/price/currency are meaningless
 * there) and fee breaks are accounting timing issues — annotate with a
 * comment instead of "fixing" them with a price edit.
 */
export const isRowEditable = (diff: PositionDiff): boolean =>
  diff.match_status !== "source_b_only" && diff.asset_type !== "fee"

/**
 * Fee rows are corrected by amount — direct market value — since merged fee
 * lines have no meaningful price × quantity (CONTEXT.md, "Correction").
 * Editable on every fee row, including Caceis-only ones: there the Scient
 * amount is empty and the operator fills it in. The CACEIS side stays
 * read-only everywhere.
 */
export const isFeeAmountEditable = (diff: PositionDiff): boolean =>
  diff.asset_type === "fee"

/**
 * Display order for diffs: unmatched rows first, then paired rows with a
 * price-gap breach (CONTEXT.md, "Breach": what to investigate), then the
 * rest of the paired rows — each block sorted by absolute market value
 * descending (Scient MV, falling back to the counterparty MV when the Scient
 * side is empty). AG Grid keeps rowData order inside asset-type groups, so
 * this global order also holds within each group. Returns positional
 * indices (not identifiers — duplicates exist for accrual/dividend rows) so
 * lookups stay 1:1 with the input array.
 *
 * Sizes and match statuses read the server's rows, so an edit that moves a
 * market value never moves the row the operator is typing in; the breach
 * reads the displayed rows (the corrected state), so a row whose edit opened
 * or closed its gap takes its new place.
 */
const isUnmatched = (d: PositionDiff): boolean =>
  d.match_status === "source_a_only" || d.match_status === "source_b_only"

// Position size for display ordering: Scient MV, counterparty MV fallback.
const displaySize = (d: PositionDiff): number => Math.abs(d.market_value_a ?? d.market_value_b ?? 0)

export const orderDiffIndices = (
  diffs: PositionDiff[],
  displayed: PositionDiff[] = diffs,
): number[] => {
  const bySizeDesc = (a: { d: PositionDiff }, b: { d: PositionDiff }) =>
    displaySize(b.d) - displaySize(a.d)
  const unmatched: { d: PositionDiff; i: number }[] = []
  const breached: { d: PositionDiff; i: number }[] = []
  const rest: { d: PositionDiff; i: number }[] = []
  diffs.forEach((d, i) => {
    if (isUnmatched(d)) unmatched.push({ d, i })
    else if (isPriceGapBreach(displayed[i] ?? d)) breached.push({ d, i })
    else rest.push({ d, i })
  })
  return [unmatched, breached, rest].flatMap(block => block.sort(bySizeDesc).map(({ i }) => i))
}

const sub = (a: number | null, b: number | null): number | null =>
  a != null && b != null ? a - b : null

// Fund-ccy MV diff for a paired row: an unvalued side counts as zero for the
// comparison, the rule one-sided rows already follow — so the grid column
// keeps adding up to the SummaryBar total when an edit leaves one side
// unvalued. Both sides unvalued stays null: nothing was compared. Mirrors the
// server's _market_value_diff (CONTEXT.md, "Market value diff").
const mvDiff = (a: number | null, b: number | null): number | null =>
  a == null && b == null ? null : (a ?? 0) - (b ?? 0)

// The page verdict (CONTEXT.md, "Reconciliation controls"), carried by the
// summary bar's accent: green when every control passed, red when a control
// breached or could not be evaluated, amber when something needs a look
// before any control can run, neutral when there is nothing to judge.
export type Verdict = "passed" | "breached" | "review" | null

/**
 * Page verdict read off the controls as the operator sees them (the
 * server's, re-evaluated only where an edit changed a class's figures or a
 * row's price) — the UI derives nothing from the 5bp split residual. Red
 * when any class breached or is unverified on the NAV control, when a
 * share-count breach stands unaccepted, or when any row breached the
 * price-gap control; green only when every control passed. Warnings and
 * missing prerequisites (no NAV control could run yet) stay amber; a fund
 * with nothing to judge stays neutral.
 */
export interface OperatorControls {
  // The NAV control per class (operatorNavControls).
  navControls: NavControlResult[]
  // The rows with each one's price-gap control (the hook's displayDiffs).
  diffs: PositionDiff[]
  // The share-count breaches still standing (unacceptedShareCountBreaches).
  shareCountBreaches: ShareCountControlResult[]
}

export const pageVerdict = (
  result: ReconciliationResult,
  { navControls, diffs, shareCountBreaches }: OperatorControls,
): Verdict => {
  if (result.share_class_warning) return "review"
  if (shareCountBreaches.length > 0) return "breached"
  if (diffs.some(isPriceGapBreach)) return "breached"
  if (navControls.length > 0) {
    return navControls.every(c => c.status === "passed") ? "passed" : "breached"
  }
  if (result.share_class_metadata !== null) return "review"
  return null
}

export const formatNav4dp = (n: number | null): string => (n === null ? "—" : n.toFixed(4))

// Basis points as shown in the strip and the breach list: two decimals, the
// precision the server reports the gap at.
export const formatBp = (bp: number | null): string =>
  bp === null ? "—" : `${bp.toFixed(2)} bp`

// The reference the NAV control paired against, named for the operator.
export const navReferenceLabel = (reference: NavReference): string =>
  reference === "published_vl" ? "CACEIS VL" : "CACEIS NAV"

const BP_PER_UNIT = 10000

/**
 * The NAV control for one class, by the server's formula
 * (controls_service._nav_control): Scient's NAV per share against the
 * published VL on a fed class, against the CACEIS-side NAV on a feedless
 * one; the exact relative gap decides against the threshold, at it passing;
 * the gap is reported at 2 dp of a basis point. No Scient NAV or a zero
 * reference is unverified. Used only where an edit changed a class's
 * figures — the server's decision stands everywhere else.
 */
export const evaluateNavControl = (
  cls: ShareClassNavResult,
  thresholdBp: number,
): NavControlResult => {
  const reference: NavReference = cls.vl_parts_caceis !== null ? "published_vl" : "caceis_nav"
  const referenceNav = reference === "published_vl" ? cls.vl_parts_caceis : cls.nav_per_share_b
  const scientNav = cls.nav_per_share_a
  const base = {
    code: cls.code,
    reference,
    nav_per_share_scient: scientNav,
    nav_per_share_reference: referenceNav,
    threshold_bp: thresholdBp,
  }
  if (scientNav === null || referenceNav === null || referenceNav === 0) {
    return { ...base, status: "unverified", gap_bp: null }
  }
  const exactGapBp = (Math.abs(scientNav - referenceNav) / Math.abs(referenceNav)) * BP_PER_UNIT
  return {
    ...base,
    status: exactGapBp <= thresholdBp ? "passed" : "breached",
    gap_bp: Math.round(exactGapBp * 100) / 100,
  }
}

/**
 * The NAV controls as the operator sees them, from the page's edit state
 * (CONTEXT.md, "Control": a control reads the corrected state): the server's
 * decision for every class an edit left alone, re-evaluated by the same rule
 * and with the thresholds the server applied for a class whose figures an
 * edit changed — a shares edit on that class, or any row value edit, which
 * moves the Scient total every class NAV follows (effectiveShareClasses).
 * Computed once by the page hook and handed to the strip and the submit
 * confirmation, so the verdict and the breach list cannot disagree. Empty
 * when the server ran no control.
 */
export const operatorNavControls = (
  result: ReconciliationResult,
  effectiveClasses: ShareClassNavResult[] | null,
  shareClassEdits: Map<string, ShareClassEdit> | undefined,
  rowValuesEdited: boolean,
): NavControlResult[] => {
  const controls = result.controls
  if (!controls || effectiveClasses === null) return controls?.nav_controls ?? []
  const serverControlByCode = new Map(controls.nav_controls.map(c => [c.code, c]))
  return effectiveClasses.map(cls => {
    const serverControl = serverControlByCode.get(cls.code)
    const edited = rowValuesEdited || shareClassEdits?.get(cls.code)?.nombre_parts !== undefined
    if (serverControl && !edited) return serverControl
    return evaluateNavControl(cls, controls.thresholds.nav_threshold_bp)
  })
}

// A NAV control that did not pass — unverified is reported like a breach
// (CONTEXT.md, "Breach"). The one client-side reading of "did not pass",
// mirroring the server's ControlReport.breaches.
export const isBreach = (ctrl: NavControlResult): boolean => ctrl.status !== "passed"

/**
 * One NAV control result spelled out: the class, both figures, the gap and
 * the threshold on a breach; what is missing when unverified. Null when the
 * class passed — nothing to spell out.
 */
export const describeNavControl = (ctrl: NavControlResult): string | null => {
  if (!isBreach(ctrl)) return null
  const ref = navReferenceLabel(ctrl.reference)
  const threshold = `threshold ${ctrl.threshold_bp} bp`
  if (ctrl.status === "breached") {
    return (
      `${ctrl.code} — NAV breach: Scient ${formatNav4dp(ctrl.nav_per_share_scient)} vs ` +
      `${ref} ${formatNav4dp(ctrl.nav_per_share_reference)}, gap ${formatBp(ctrl.gap_bp)} ` +
      `(${threshold})`
    )
  }
  const reason =
    ctrl.nav_per_share_scient === null
      ? "no Scient NAV"
      : ctrl.nav_per_share_reference === null
        ? `no ${ref}`
        : `${ref} is zero`
  return (
    `${ctrl.code} — NAV unverified: ${reason} ` +
    `(Scient ${formatNav4dp(ctrl.nav_per_share_scient)}, ${ref} ` +
    `${formatNav4dp(ctrl.nav_per_share_reference)}, ${threshold})`
  )
}

// --- Share-count control (#1751) ------------------------------------------

// A share-count control that did not pass (CONTEXT.md, "Breach"): the
// file's count is not the known count.
export const isShareCountBreach = (ctrl: ShareCountControlResult): boolean =>
  ctrl.status !== "passed"

// The share-count control for one class as the operator sees it: passed;
// breached by the file's count moving from the known one (standing until
// accepted); accepted — that breach cleared by the acceptance; edited —
// breached by the operator's own count, which no acceptance clears; or
// unverified — a fed class the file carries no count for, nothing to accept.
export type ShareCountState = "passed" | "breached" | "accepted" | "edited" | "unverified"

// A shares edit to anything but the file's figure: on a fed fund the file's
// count is the count, so such an edit breaches (CONTEXT.md, "Share-count
// acceptance") and only reverting it clears the breach. The server reads
// the same rule at submit (controls_service._share_count_control, on the
// edit the payload carries) and refuses regardless of this gate.
export const editedAwayFromFile = (
  ctrl: ShareCountControlResult,
  edit: ShareClassEdit | undefined,
): boolean => edit?.nombre_parts !== undefined && edit.nombre_parts !== ctrl.file_nombre_parts

/**
 * The share-count control's state for one class. The server's file-vs-known
 * result is never re-evaluated — both counts are its facts; the operator's
 * edit is judged here against the file's figure, as the server judges it at
 * submit. An acceptance clears only the file-vs-known breach. The strip's
 * cell, the standing-breach list, the submit gate and the verdict all read
 * this one state.
 */
export const shareCountState = (
  ctrl: ShareCountControlResult,
  acceptance: ShareCountAcceptanceEdit | undefined,
  edit: ShareClassEdit | undefined,
): ShareCountState => {
  if (ctrl.status === "unverified") return "unverified"
  if (editedAwayFromFile(ctrl, edit)) return "edited"
  if (!isShareCountBreach(ctrl)) return "passed"
  return acceptance !== undefined ? "accepted" : "breached"
}

// The states in which a share-count breach stands: the file's count moved
// and nobody accepted it, the operator's count is not the file's, or the
// file carries no count for a fed class.
const STANDING_SHARE_COUNT_STATES: ReadonlySet<ShareCountState> = new Set([
  "breached",
  "edited",
  "unverified",
])

/**
 * The share-count breaches still standing. While one stands the submit
 * button is disabled and the verdict is red; the server refuses regardless.
 */
export const unacceptedShareCountBreaches = (
  controls: ControlReport | null,
  acceptances: Map<string, ShareCountAcceptanceEdit>,
  edits: Map<string, ShareClassEdit>,
): ShareCountControlResult[] =>
  (controls?.share_count_controls ?? []).filter(ctrl =>
    STANDING_SHARE_COUNT_STATES.has(
      shareCountState(ctrl, acceptances.get(ctrl.code), edits.get(ctrl.code)),
    ),
  )

/**
 * Submission payload block for the share-count acceptances (CONTEXT.md):
 * the class and the comment per accepted class, the counts being the
 * server's to look up. Undefined when nothing was accepted.
 */
export const buildShareCountAcceptances = (
  acceptances: Map<string, ShareCountAcceptanceEdit>,
): ShareCountAcceptance[] | undefined =>
  acceptances.size === 0
    ? undefined
    : [...acceptances].map(([code, { comment }]) => ({ code, comment }))

// --- Price-gap control (#1750) --------------------------------------------

// Rows the price-gap control never looks at: cash, fees, dividends and
// accruals are not securities. Mirrors the server's _NOT_A_SECURITY.
const NOT_A_SECURITY = new Set(["cash", "fee", "dividend", "accrual"])

const roundTo = (n: number, decimals: number): number => {
  const scale = 10 ** decimals
  return Math.round((n + Number.EPSILON) * scale) / scale
}

export type PriceGapControl = Pick<
  PositionDiff,
  "price_gap_pct" | "price_gap_value" | "price_gap_status"
>

const OUTSIDE_PRICE_GAP_CONTROL: PriceGapControl = {
  price_gap_pct: null,
  price_gap_value: null,
  price_gap_status: null,
}

/**
 * The price-gap control for one row, by the server's formula
 * (controls_service._price_gap_control), read on each side's unit price in
 * fund currency — market value / quantity — so the quoting convention, the
 * FX and the multiplier never count as a gap (#1755): relative gap =
 * |Scient unit price − CACEIS unit price| / |CACEIS unit price|; gap value
 * = |CACEIS quantity × unit price diff|, already in fund currency; breach
 * only when the gap is over the price-gap threshold AND the gap value's
 * share of the NAV reference (the CACEIS total of the compare) is over the
 * NAV-share threshold. Cash, fees, dividends, accruals, a security
 * priced on one side only and a line sold out on both sides are outside
 * the control; a missing or zero unit price is
 * unverified with no gap, an unknown CACEIS quantity or NAV reference
 * unverified with the gap still reported. The exact figures decide; the
 * reported ones are rounded as the server rounds them. Used only for a
 * row whose price an edit changed — the server's decision stands
 * everywhere else.
 */
export const evaluatePriceGapControl = (
  row: PositionDiff,
  thresholds: ControlThresholds,
  navReference: number,
): PriceGapControl => {
  if (row.asset_type !== null && NOT_A_SECURITY.has(row.asset_type)) {
    return OUTSIDE_PRICE_GAP_CONTROL
  }
  if (row.price_a === null || row.price_b === null) return OUTSIDE_PRICE_GAP_CONTROL
  // Sold out on both sides, still listed: nothing is priced, nothing to
  // acknowledge — outside, not unverified.
  if (row.quantity_a === 0 && row.quantity_b === 0) return OUTSIDE_PRICE_GAP_CONTROL
  if (row.unit_price_a === null || row.unit_price_b === null || row.unit_price_b === 0) {
    return { price_gap_pct: null, price_gap_value: null, price_gap_status: "unverified" }
  }
  const priceDiff = row.unit_price_a - row.unit_price_b
  const exactGapPct = (Math.abs(priceDiff) / Math.abs(row.unit_price_b)) * 100
  const gapPct = roundTo(exactGapPct, 4)
  if (row.quantity_b === null || !navReference) {
    return { price_gap_pct: gapPct, price_gap_value: null, price_gap_status: "unverified" }
  }
  const exactGapValue = Math.abs(row.quantity_b * priceDiff)
  const exactNavSharePct = (exactGapValue / Math.abs(navReference)) * 100
  const breached =
    exactGapPct > thresholds.price_gap_threshold_pct &&
    exactNavSharePct > thresholds.nav_share_threshold_pct
  return {
    price_gap_pct: gapPct,
    price_gap_value: roundTo(exactGapValue, 2),
    price_gap_status: breached ? "breached" : "passed",
  }
}

/**
 * The price-gap control for one row as the operator sees it (CONTEXT.md,
 * "Control": a control reads the corrected state): the server's decision
 * carried on the row, re-evaluated by the same rule and with the thresholds
 * and NAV reference the server applied only when an edit changed the row's
 * price. A quantity or comment edit moves no price and leaves the server's
 * result; so does a fund the server ran no controls for.
 */
export const operatorPriceGapControl = (
  row: PositionDiff,
  override: RowOverride | undefined,
  controls: ControlReport | null,
  navReference: number,
): PositionDiff => {
  if (!controls || override?.override_price === undefined) return row
  return { ...row, ...evaluatePriceGapControl(row, controls.thresholds, navReference) }
}

// A row whose price-gap control did not pass — unverified is reported like
// a breach (CONTEXT.md, "Breach"). A row outside the control is not one.
export const isPriceGapBreach = (row: PositionDiff): boolean =>
  row.price_gap_status !== null && row.price_gap_status !== "passed"

// The price-gap breaches as the operator sees them, with what reading one
// needs: the thresholds and the NAV reference (the CACEIS total of the
// compare) the control ran with. Owned by the page hook; absent when the
// server ran no controls, so a reader never meets a breach without its
// thresholds.
export type PriceGapBreaches = {
  rows: PositionDiff[]
  thresholds: ControlThresholds
  navReference: number
}

// A relative gap as the grid and the bar show it: two decimals of a percent.
// Undefined too: AG Grid formats group rows and rows outside the control,
// whose cells carry no value, and a formatter that throws there unmounts
// the grid the moment the column scrolls into view.
export const formatPct = (pct: number | null | undefined): string =>
  pct == null ? "—" : `${pct.toFixed(2)}%`

// A count with its noun: "1 class", "2 securities".
export const plural = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`

// A price as the breach tables show it: up to four decimals, no float noise.
export const formatPrice = (price: number | null): string =>
  price === null ? "—" : price.toLocaleString("en-US", { maximumFractionDigits: 4 })

// Why the control could not value the gap, read off the inputs the
// evaluator needs (evaluatePriceGapControl checks them in this order).
const unverifiedReason = (row: PositionDiff, navReference: number): string => {
  const noUnitPrice = [
    ...(row.unit_price_a === null ? ["Scient"] : []),
    ...(row.unit_price_b === null ? ["CACEIS"] : []),
  ]
  if (noUnitPrice.length > 0) {
    const noun = noUnitPrice.length > 1 ? "unit prices" : "unit price"
    return `${noUnitPrice.join(" and ")} ${noun} unknown (no market value or zero quantity)`
  }
  if (row.unit_price_b === 0) return "CACEIS unit price is zero"
  if (row.quantity_b === null) return "CACEIS quantity unknown"
  if (!navReference) return "no NAV reference"
  return "gap value unknown"
}

/**
 * One row's price-gap control spelled out: the quotes as each side writes
 * them, the unit prices in fund currency the gap is read on, the gap, the
 * gap value and its share of the NAV, with the thresholds applied; what is
 * missing when unverified. Null for a row outside the control.
 */
export const describePriceGapControl = (
  row: PositionDiff,
  thresholds: ControlThresholds,
  navReference: number,
): string | null => {
  if (row.price_gap_status === null) return null
  const prices =
    `quoted Scient ${row.price_a ?? "—"} vs CACEIS ${row.price_b ?? "—"}, ` +
    `in fund ccy Scient ${formatPrice(row.unit_price_a)} vs CACEIS ${formatPrice(row.unit_price_b)}`
  const gap = `gap ${formatPct(row.price_gap_pct)} (threshold ${thresholds.price_gap_threshold_pct}%)`
  if (row.price_gap_status === "unverified") {
    return `${row.identifier} — price gap unverified: ${unverifiedReason(row, navReference)} (${prices}, ${gap})`
  }
  const navShare = navReference
    ? formatPct(((row.price_gap_value ?? 0) / Math.abs(navReference)) * 100)
    : "—"
  const value =
    `gap value ${formatNumber(row.price_gap_value)} = ${navShare} of NAV ` +
    `(threshold ${thresholds.nav_share_threshold_pct}%)`
  const label = row.price_gap_status === "breached" ? "price-gap breach" : "price gap passed"
  return `${row.identifier} — ${label}: ${prices}, ${gap}, ${value}`
}

export const applyOverride = (
  diff: PositionDiff,
  override: RowOverride | undefined,
): PositionDiff => {
  // Always return a fresh clone so each rowData prop has every row at a new
  // object reference. With readOnlyEdit + getRowId, AG Grid still does its
  // immutable diff by ID, but sharing the original reference for unedited
  // rows was triggering a RowCtrl.updateRowIndexes crash in v27 (null DOM
  // classList) — likely because AG Grid retained the old reference internally
  // and got confused when only some rows had new identity.
  if (!override) return { ...diff }
  const effPrice = override.override_price ?? diff.price_a
  const effQty = override.override_quantity ?? diff.quantity_a

  // Single MV formula (#1619): trade-ccy MV = qty × price × multiplier,
  // fund-ccy MV = trade-ccy MV × fx — using the valuation factors delivered
  // on the row. Cash rows carry no unit price; theirs is 1 by definition
  // (CONTEXT.md, "unvalued position"). When a factor (or the price) is
  // unknown the MV cells stay as-is — empty for unvalued rows — and the
  // override is still recorded and sent on submit.
  // A direct amount correction (fee rows) replaces the fund-ccy MV outright
  // — no price × quantity path exists for merged fee lines. Takes precedence:
  // an override carrying both an amount and price/qty (impossible through the
  // grid, whose editability guards are exclusive by asset_type) would have its
  // price/qty parts ignored here.
  if (override.override_market_value !== undefined) {
    const newMv = override.override_market_value
    return {
      ...diff,
      market_value_a: newMv,
      market_value_diff: mvDiff(newMv, diff.market_value_b),
      ...(override.comment !== undefined ? { comment: override.comment } : {}),
    } as PositionDiff
  }

  const mult = diff.multiplication_factor
  const fx = diff.fx_rate
  const priceForMv = effPrice ?? (diff.asset_type === "cash" ? 1 : null)
  let newMv: number | null = diff.market_value_a
  let newLocalMv: number | null = diff.market_value_local_a
  // The Scient unit price in fund currency (market value / quantity) the
  // price-gap control reads (#1755) follows the valuation: recomputed with
  // the market value (a quantity edit lands on the same figure, since the
  // market value scales with it); unknown when a corrected price could not
  // be valued (the control then reports unverified); the server's figure
  // stays when a quantity edit could not be valued either.
  let newUnitPrice: number | null = diff.unit_price_a
  if (mult != null && fx != null && priceForMv != null && effQty != null) {
    newLocalMv = effQty * priceForMv * mult
    newMv = newLocalMv * fx
    newUnitPrice = effQty !== 0 ? newMv / effQty : null
  } else if (override.override_price !== undefined) {
    newUnitPrice = null
  }

  // Implied FX re-derived from the scaled pair (invariant under pure scaling).
  const newImpliedFxA =
    newMv != null && newLocalMv != null && newLocalMv !== 0 ? newMv / newLocalMv : null

  return {
    ...diff,
    price_a: effPrice,
    quantity_a: effQty,
    price_diff: sub(effPrice, diff.price_b),
    quantity_diff: sub(effQty, diff.quantity_b),
    market_value_a: newMv,
    market_value_diff: mvDiff(newMv, diff.market_value_b),
    market_value_local_a: newLocalMv,
    market_value_local_diff: sub(newLocalMv, diff.market_value_local_b),
    implied_fx_a: newImpliedFxA,
    unit_price_a: newUnitPrice,
    ...(override.comment !== undefined ? { comment: override.comment } : {}),
  } as PositionDiff
}

/**
 * Key for one corrected cell in the correction-history map: the row's
 * identifier plus the edited field.
 */
export const correctionKey = (target: string, field: string): string =>
  `${target}|${field}`

/**
 * Group correction audit-log entries by (target, field) so the grid can look
 * up the chain behind one cell. Entries keep the endpoint's chain order
 * (oldest first).
 */
export const buildCorrectionHistoryMap = (
  entries: CorrectionEntry[],
): Map<string, CorrectionEntry[]> => {
  const map = new Map<string, CorrectionEntry[]>()
  for (const entry of entries) {
    const key = correctionKey(entry.target, entry.field)
    const existing = map.get(key)
    if (existing) existing.push(entry)
    else map.set(key, [entry])
  }
  return map
}

/**
 * Render one cell's correction chain as tooltip text: who changed it, from
 * what to what, when, and why — one line per entry, oldest first.
 */
export const formatCorrectionHistory = (entries: CorrectionEntry[]): string =>
  entries
    .map(entry => {
      const when = entry.created_at.slice(0, 16).replace("T", " ")
      const base = `${entry.old_value ?? "-"} → ${entry.new_value} — ${entry.author}, ${when}`
      return entry.comment ? `${base} — ${entry.comment}` : base
    })
    .join("\n")

/**
 * Apply an edited shares outstanding to one share-class result (#1631).
 * The class value is invariant under a share-count correction — new shares
 * change how the pie is cut, not its size — so each NAV rescales by
 * parts_old / parts_new. The CACEIS-published VL is never edited; the delta
 * recomputes against it. Non-positive or unchanged edits are ignored.
 */
export const applyShareClassOverride = (
  cls: ShareClassNavResult,
  editedParts: number | undefined,
): ShareClassNavResult => {
  if (editedParts == null || !(editedParts > 0) || editedParts === cls.nombre_parts) {
    return { ...cls }
  }
  const rescale = (nav: number | null): number | null =>
    nav === null ? null : (nav * cls.nombre_parts) / editedParts
  const navB = rescale(cls.nav_per_share_b)
  return {
    ...cls,
    nombre_parts: editedParts,
    nav_per_share_a: rescale(cls.nav_per_share_a),
    nav_per_share_b: navB,
    diff_vs_caceis:
      navB !== null && cls.vl_parts_caceis !== null ? navB - cls.vl_parts_caceis : null,
  }
}

/**
 * Submission payload block for the edited share counts (#1631): one override
 * per edited class, carrying the pre-edit value as the correction baseline —
 * the file figure for fed funds, the carried-forward value for feedless ones.
 * Undefined when nothing was edited.
 */
export const buildShareClassOverrides = (
  shareClasses: ShareClassNavResult[] | null,
  edits: Map<string, ShareClassEdit>,
): ShareClassOverride[] | undefined => {
  if (!shareClasses || edits.size === 0) return undefined
  const out: ShareClassOverride[] = []
  for (const cls of shareClasses) {
    const edit = edits.get(cls.code)
    if (edit?.nombre_parts === undefined) continue
    out.push({
      code: cls.code,
      nombre_parts: String(edit.nombre_parts),
      original_nombre_parts: String(cls.nombre_parts),
      comment: edit.comment ?? "",
    })
  }
  return out.length > 0 ? out : undefined
}

// The server's NAV rounding (share_class_nav_service, _NAV_QUANTUM): four
// decimals, half away from zero.
const roundNav4dp = (nav: number): number =>
  (Math.sign(nav) * Math.round((Math.abs(nav) + Number.EPSILON) * 10_000)) / 10_000

// The fund totals the class NAVs follow once a row value was edited: the
// Scient total and, within it, the fee rows (asset_type "fee"); the per-share
// figures a feedless single-class fund maps straight onto its class.
export interface EffectiveTotals {
  total_market_value_a: number
  scient_fee_total_a: number
  nav_per_share_a: number | null
  nav_per_share_b: number | null
}

/**
 * The share-class rows as the operator sees them (#1631): edited share
 * counts rescale each class's NAVs; a feedless single-class fund then reads
 * its NAVs off the (possibly edited) fund totals instead — the whole fund is
 * the one class, so totals ÷ shares outstanding IS the class NAV. On a fed
 * multi-class fund a row value edit moves the Scient total, so every class's
 * Scient NAV is recomputed by the server's formula
 * (compute_share_class_navs): (portfolio value × split − class fee) ÷ shares,
 * where the portfolio value is the Scient total less its fee rows, at 4 dp
 * half up; the CACEIS side is not Scient's to edit and stays. Single source
 * for the summary bar display and the NAVs echoed at submit.
 */
export const effectiveShareClasses = (
  result: ReconciliationResult,
  edits: Map<string, ShareClassEdit> | undefined,
  totals: EffectiveTotals | null | undefined,
  rowValuesEdited: boolean,
): ShareClassNavResult[] | null => {
  const { share_classes, share_class_metadata } = result
  if (share_classes === null) return null
  const edited = share_classes.map(cls =>
    applyShareClassOverride(cls, edits?.get(cls.code)?.nombre_parts),
  )
  const isFeedlessSingleClass =
    share_class_metadata === null && share_classes.length === 1
  if (isFeedlessSingleClass && totals) {
    return [
      {
        ...edited[0],
        nav_per_share_a: totals.nav_per_share_a,
        nav_per_share_b: totals.nav_per_share_b,
      },
    ]
  }
  if (share_class_metadata !== null && rowValuesEdited && totals) {
    // No Scient total, no Scient NAV — as the server has none (step 8).
    const portfolioValueA = totals.total_market_value_a
      ? totals.total_market_value_a - totals.scient_fee_total_a
      : null
    return edited.map(cls => ({
      ...cls,
      nav_per_share_a:
        portfolioValueA === null || cls.nombre_parts === 0
          ? null
          : roundNav4dp((portfolioValueA * cls.split_pct - cls.fee_amount) / cls.nombre_parts),
    }))
  }
  return edited
}
